import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAccountMode } from '../../context/AccountModeContext';
import { alpacaService, Position, ScanResult, WatchlistItem } from '../../services/AlpacaService';
import styles from './Market.module.css';

const FALLBACK_SYMBOLS = ['SPY', 'QQQ', 'DIA', 'IWM'];

function isMarketOpen(): boolean {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const h = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
  const m = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);
  const day = parts.find((p) => p.type === 'weekday')?.value || '';
  if (['Sat', 'Sun'].includes(day)) return false;
  const t = h * 60 + m;
  return t >= 570 && t < 960;
}

function rsiColor(val: number | null): string {
  if (val === null) return '';
  if (val >= 70) return styles.rsiOverbought;
  if (val <= 30) return styles.rsiOversold;
  return styles.rsiNeutral;
}

function signalBadgeClass(signal: string): string {
  switch (signal) {
    case 'OVERBOUGHT':
      return styles.signalOverbought;
    case 'OVERSOLD':
      return styles.signalOversold;
    case 'STRONG':
      return styles.signalStrong;
    case 'WEAK':
      return styles.signalWeak;
    default:
      return styles.signalNeutral;
  }
}

const Market: React.FC = () => {
  const { isDark } = useTheme();
  const { mode } = useAccountMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [symbol, setSymbol] = useState('SPY');
  const [inputValue, setInputValue] = useState('');
  const [positions, setPositions] = useState<Position[]>([]);
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [viewMode, setViewMode] = useState<'single' | 'grid'>('single');
  const [watchlists, setWatchlists] = useState<WatchlistItem[]>([]);
  const [activeWatchlist, setActiveWatchlist] = useState('');

  // Fetch watchlists
  useEffect(() => {
    alpacaService
      .getWatchlists()
      .then((res) => {
        setWatchlists(res?.watchlists || []);
      })
      .catch(() => {});
  }, []);

  // Separate positions by side
  const shorts = positions.filter((p) => p.side === 'short');
  const longs = positions.filter((p) => p.side === 'long');
  const positionSymbols = useMemo(() => [...shorts, ...longs].map((p) => p.symbol), [shorts, longs]);
  const symsKey = positionSymbols.join(',');
  const wlSymbols = watchlists.find((w) => w.name === activeWatchlist)?.symbols || [];
  const quickSymbols =
    activeWatchlist && wlSymbols.length > 0
      ? wlSymbols
      : positionSymbols.length > 0
        ? positionSymbols
        : FALLBACK_SYMBOLS;
  const gridSymbolsKey = quickSymbols.slice(0, 6).join(',');
  const gridSymbols = useMemo(() => quickSymbols.slice(0, 6), [gridSymbolsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build a lookup: symbol -> scan data
  const scanMap = new Map(scanResults.map((r) => [r.symbol, r]));
  // Build a lookup: symbol -> position data
  const posMap = new Map(positions.map((p) => [p.symbol, p]));

  // Initial fetch
  useEffect(() => {
    alpacaService
      .getPositions(mode)
      .then((res) => {
        setPositions(res.positions || []);
      })
      .catch(() => {});
  }, [mode]);

  // Once we have positions, fetch RSI + set up refresh
  useEffect(() => {
    if (positionSymbols.length === 0) return;
    const doScan = () => {
      alpacaService
        .scanRsi(symsKey)
        .then((res) => {
          if (res.results) setScanResults(res.results);
        })
        .catch(() => {});
    };
    doScan();
    const interval = isMarketOpen() ? 15_000 : 60_000;
    const id = setInterval(doScan, interval);
    return () => clearInterval(id);
  }, [symsKey, positionSymbols.length]);

  // Render single chart
  useEffect(() => {
    if (viewMode !== 'single') return;
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol,
      interval: 'D',
      timezone: 'America/New_York',
      theme: isDark ? 'dark' : 'light',
      style: '1',
      locale: 'en',
      allow_symbol_change: true,
      calendar: false,
      support_host: 'https://www.tradingview.com',
      studies: [
        { id: 'RSI@tv-basicstudies', inputs: { length: 2 } },
        { id: 'RSI@tv-basicstudies', inputs: { length: 14 } },
      ],
    });
    container.appendChild(script);
  }, [symbol, isDark, viewMode]);

  // Render grid charts
  useEffect(() => {
    if (viewMode !== 'grid') return;
    gridRefs.current.forEach((el, i) => {
      if (!el) return;
      el.innerHTML = '';
      const sym = gridSymbols[i];
      if (!sym) return;
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
      script.type = 'text/javascript';
      script.async = true;
      script.innerHTML = JSON.stringify({
        autosize: true,
        symbol: sym,
        interval: 'D',
        timezone: 'America/New_York',
        theme: isDark ? 'dark' : 'light',
        style: '1',
        locale: 'en',
        allow_symbol_change: false,
        calendar: false,
        hide_top_toolbar: true,
        hide_side_toolbar: true,
        support_host: 'https://www.tradingview.com',
        studies: [{ id: 'RSI@tv-basicstudies', inputs: { length: 14 } }],
      });
      el.appendChild(script);
    });
  }, [viewMode, isDark, gridSymbols]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const val = inputValue.trim().toUpperCase();
    if (val) {
      setSymbol(val);
      setViewMode('single');
      setInputValue('');
    }
  };

  // Combine scan + position data for sidebar rows
  const sidebarRows = quickSymbols.map((sym) => {
    const scan = scanMap.get(sym);
    const pos = posMap.get(sym);
    return {
      symbol: sym,
      side: pos?.side || null,
      qty: pos ? parseFloat(pos.qty) : null,
      pnl: pos ? parseFloat(pos.unrealized_pl) : null,
      rsi2: scan?.rsi2 ?? null,
      rsi6: scan?.rsi6 ?? null,
      rsi14: scan?.rsi14 ?? null,
      signal: scan?.signal || 'NEUTRAL',
      price: scan?.price ?? (pos ? parseFloat(pos.current_price) : null),
      changePct: scan?.change_pct ?? null,
    };
  });

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Market</h1>
        <div className={styles.headerControls}>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewBtn} ${viewMode === 'single' ? styles.viewBtnActive : ''}`}
              onClick={() => setViewMode('single')}
            >
              Chart
            </button>
            <button
              className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnActive : ''}`}
              onClick={() => setViewMode('grid')}
            >
              Grid
            </button>
          </div>
          <form className={styles.symbolForm} onSubmit={handleSubmit}>
            <input
              className={styles.symbolInput}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={symbol}
              title="Chart symbol"
            />
            <button className={styles.goBtn} type="submit">
              Go
            </button>
          </form>
        </div>
      </div>

      {/* Watchlist selector */}
      {watchlists.length > 0 && (
        <div className={styles.watchlistBar}>
          <select
            className={styles.watchlistSelect}
            value={activeWatchlist}
            onChange={(e) => setActiveWatchlist(e.target.value)}
            title="Select watchlist"
          >
            <option value="">Positions</option>
            {watchlists.map((w) => (
              <option key={w.name} value={w.name}>
                {w.name} ({w.symbols.length})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Quick buttons */}
      <div className={styles.quickSymbols}>
        {shorts.length > 0 &&
          shorts.map((p) => (
            <button
              key={p.symbol}
              className={`${styles.quickBtn} ${styles.quickBtnShort} ${p.symbol === symbol && viewMode === 'single' ? styles.quickBtnActive : ''}`}
              onClick={() => {
                setSymbol(p.symbol);
                setViewMode('single');
              }}
            >
              {p.symbol}
            </button>
          ))}
        {longs.length > 0 &&
          longs.map((p) => (
            <button
              key={p.symbol}
              className={`${styles.quickBtn} ${styles.quickBtnLong} ${p.symbol === symbol && viewMode === 'single' ? styles.quickBtnActive : ''}`}
              onClick={() => {
                setSymbol(p.symbol);
                setViewMode('single');
              }}
            >
              {p.symbol}
            </button>
          ))}
        {positions.length === 0 &&
          FALLBACK_SYMBOLS.map((s) => (
            <button
              key={s}
              className={`${styles.quickBtn} ${s === symbol && viewMode === 'single' ? styles.quickBtnActive : ''}`}
              onClick={() => {
                setSymbol(s);
                setViewMode('single');
              }}
            >
              {s}
            </button>
          ))}
      </div>

      <div className={styles.mainContent}>
        {/* Chart area */}
        <div className={styles.chartArea}>
          {viewMode === 'single' ? (
            <div className={styles.chartWrapper}>
              <div className="tradingview-widget-container" style={{ height: '100%', width: '100%' }}>
                <div
                  className="tradingview-widget-container__widget"
                  ref={containerRef}
                  style={{ height: 'calc(100% - 32px)', width: '100%' }}
                />
              </div>
            </div>
          ) : (
            <div className={styles.chartGrid}>
              {gridSymbols.map((sym, i) => (
                <div key={sym} className={styles.gridCell}>
                  <div className={styles.gridCellLabel}>{sym}</div>
                  <div className="tradingview-widget-container" style={{ height: '100%', width: '100%' }}>
                    <div
                      className="tradingview-widget-container__widget"
                      ref={(el) => {
                        gridRefs.current[i] = el;
                      }}
                      style={{ height: 'calc(100% - 28px)', width: '100%' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RSI Sidebar */}
        {sidebarRows.length > 0 && (
          <div className={styles.sidebar}>
            <div className={styles.sidebarTitle}>Positions RSI</div>
            <div className={styles.sidebarTable}>
              <div className={styles.sidebarHeader}>
                <span>Symbol</span>
                <span>RSI(2)</span>
                <span>RSI(14)</span>
                <span>Signal</span>
                <span>P&L</span>
              </div>
              {sidebarRows.map((row) => (
                <div
                  key={row.symbol}
                  className={`${styles.sidebarRow} ${row.symbol === symbol && viewMode === 'single' ? styles.sidebarRowActive : ''}`}
                  onClick={() => {
                    setSymbol(row.symbol);
                    setViewMode('single');
                  }}
                >
                  <span className={styles.sidebarSymbol}>
                    {row.symbol}
                    {row.side && (
                      <span className={row.side === 'short' ? styles.sideShort : styles.sideLong}>
                        {row.side === 'short' ? 'S' : 'L'}
                      </span>
                    )}
                  </span>
                  <span className={rsiColor(row.rsi2)}>{row.rsi2 !== null ? row.rsi2.toFixed(1) : '—'}</span>
                  <span className={rsiColor(row.rsi6)}>{row.rsi6 !== null ? row.rsi6.toFixed(1) : '—'}</span>
                  <span className={rsiColor(row.rsi14)}>{row.rsi14 !== null ? row.rsi14.toFixed(1) : '—'}</span>
                  <span className={signalBadgeClass(row.signal)}>{row.signal}</span>
                  <span className={row.pnl !== null ? (row.pnl >= 0 ? styles.positive : styles.negative) : ''}>
                    {row.pnl !== null ? `$${row.pnl.toFixed(2)}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Market;
